import { Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FilterCondition } from 'framework/types';
import { AnalyticalTableLayout, type AnalyticalTableColumn } from 'framework/ui/ui-builder';
import { POSITION_SLOTS_PATH } from 'domain/people/positionSlots/constants';
import {
  WORKFORCE_ANALYTICS_COLUMN_LABELS,
  WORKFORCE_ANALYTICS_COLUMNS,
  WORKFORCE_ANALYTICS_HIGH_FILL_RATE,
  type WorkforceAnalyticsColumn,
} from 'domain/people/workforceAnalytics/constants';
import type { WorkforceAnalyticsCell, WorkforceAnalyticsRow } from 'domain/people/workforceAnalytics/types';
import { formatWorkforceAnalyticsCell } from 'domain/people/workforceAnalytics/utils';

type WorkforceAnalyticsTableProps = {
  rows: WorkforceAnalyticsRow[];
};

const WORKFORCE_ANALYTICS_TABLE_COLUMNS: AnalyticalTableColumn<WorkforceAnalyticsColumn>[] =
  WORKFORCE_ANALYTICS_COLUMNS.map(column => ({
    id: column,
    label: WORKFORCE_ANALYTICS_COLUMN_LABELS[column],
  }));

const buildRelatedPositionSlotsFilter = (positionSlotIds: string[]) => ({
  id: {
    [FilterCondition.IN]: positionSlotIds,
  },
});

const getValueCellBackgroundColor = (cell: WorkforceAnalyticsCell) => {
  if (!cell.total) return 'var(--mui-palette-common-white)';
  if (cell.fillRate >= WORKFORCE_ANALYTICS_HIGH_FILL_RATE) return 'var(--mui-palette-background-success)';

  return 'var(--mui-palette-background-warning)';
};

const WorkforceAnalyticsTooltip = ({ cell }: { cell: WorkforceAnalyticsCell }) => (
  <Stack sx={{ gap: 1 }}>
    <Typography>Filled position slots: {cell.filled}</Typography>
    <Typography>All position slots: {cell.total}</Typography>
    <Typography>Fill rate: {cell.fillRate}%</Typography>
  </Stack>
);

const WorkforceAnalyticsTable = ({ rows }: WorkforceAnalyticsTableProps) => {
  const navigate = useNavigate();

  const handleOpenRelatedPositionSlots = (positionSlotIds: string[]) => {
    const filter = buildRelatedPositionSlotsFilter(positionSlotIds);

    navigate(`${POSITION_SLOTS_PATH}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
  };

  return (
    <AnalyticalTableLayout
      columns={WORKFORCE_ANALYTICS_TABLE_COLUMNS}
      rows={rows}
      renderCell={({ cell }) => formatWorkforceAnalyticsCell(cell)}
      renderCellTooltip={({ cell }) => (cell.total ? <WorkforceAnalyticsTooltip cell={cell} /> : null)}
      getCellBackgroundColor={({ cell }) => getValueCellBackgroundColor(cell)}
      isCellInteractive={({ cell }) => cell.positionSlotIds.length > 0}
      onCellClick={({ cell }) => handleOpenRelatedPositionSlots(cell.positionSlotIds)}
    />
  );
};

export default WorkforceAnalyticsTable;
